/**
 * AI Risk Index (Live) — our real-time read of Stanford HAI's AI Index.
 *
 * Three live surfaces (real-time from our feeds) + one static surface
 * (Stanford 2026 headline figures, seeded into CyberClaimsBenchmark on
 * startup):
 *
 *   - /api/ai-risk-index/incidents          (Surface A — live)
 *   - /api/ai-risk-index/threat-actors      (Surface B — live)
 *   - /api/ai-risk-index/toxic-paths        (Surface B — live, 9th pattern)
 *   - /api/ai-risk-index/financial          (Surface C — live, BoE lens)
 *   - /api/ai-risk-index/stanford-findings  (Static — Stanford 2026)
 *   - /api/frontier/hf-papers (existing)    (Live — HF capability proxy)
 *
 * LIVE tiles get a cyan border + LIVE badge; STATIC tiles get an amber
 * border + STATIC badge. Tiles render gracefully empty if their feed
 * has no data yet (never blank or crash).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Activity, AlertTriangle, Brain, Building2, ClipboardCopy, Cpu, Database,
  Download, ExternalLink, FileText, Network, RefreshCw, ShieldCheck, Skull, Zap,
  type LucideIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'
import MiniBarChart from '../components/MiniBarChart'
import PageHeader from '../components/PageHeader'

// ── Response types ──────────────────────────────────────────────────────────

interface IncidentsResp {
  total: number
  ytd: number
  prev_year_same_window: number
  yoy_delta_pct: number
  by_sector: Record<string, number>
  by_kind: Record<string, number>
  by_source?: Record<string, number>
  sparkline_30d: number[]
  latest_5: { title: string; date: string | null; sector: string; kind: string; source_url: string | null; sources?: string[] }[]
  generated_at: string
}

interface FrontierVelocityResp {
  arxiv: {
    total: number
    daily_counts: number[]
    moving_avg_7d: number[]
    categories?: string[]
    days?: number
  }
  hf_releases: {
    total: number
    top_5: { model_id: string; downloads: number }[]
    daily_counts?: number[]
    days?: number
  }
  github: {
    repos: {
      repo: string
      current_stars?: number
      stars: number
      delta_30d: number
      delta_partial?: boolean
      url: string
    }[]
    total_stars: number
    total_delta_30d: number
    auth?: boolean
    warning?: string | null
  }
  generated_at: string
}

interface ThreatActorsResp {
  total_actors: number
  uses_ai_count: number
  most_recent_5: {
    id: string
    name: string
    aliases: string[]
    sponsor_country: string
    category: string
    last_seen: string | null
    ai_evidence: string | null
  }[]
  generated_at: string
}

interface ToxicPathsResp {
  paths: { id: string; title: string; score: number; narrative: string }[]
  count: number
  note?: string
  generated_at: string
}

interface FinancialResp {
  sector: string
  filter_source?: string | null
  counts: { '30d': number; '60d': number; '365d': number }
  top_boe_relevant: { title: string; date: string | null; kind: string; source: string; source_url: string | null }[]
  top_control_gaps: { label: string; hits: number }[]
  total_finance: number
  by_source?: Record<string, number>
  // Merged regulator-row stream — populated from financial_lens.items.
  // When ?source= is set on the request, this is restricted to a single
  // regulator. When unset, every wired regulator is folded in.
  items?: { title: string; date: string | null; kind: string; source: string; source_url: string | null }[]
  generated_at: string
}

interface StanfordFinding {
  metric: string
  value: number
  unit: string
  notes: string
  source_url: string
}
interface StanfordResp {
  year: number
  source: string
  source_url: string
  count: number
  findings: StanfordFinding[]
  generated_at: string
  from?: string
}

interface HfPapersResp {
  count?: number
  papers?: { id?: string; title?: string }[]
  fetched_at?: string
}

// ── AISI Evaluations types (Track 24) ──────────────────────────────────────

interface AISIEvaluationRow {
  id: number
  source: string             // uk_aisi | us_aisi | jp_aisi | eu_ai_office
  model: string
  model_version: string | null
  cap_area: string           // cyber | bio | chem | autonomy | persuasion | general
  publication_date: string | null
  pdf_url: string
  title: string
  summary: string | null
  scenarios_tested: string[]
  highest_observed: string | null
  meets_threshold: boolean
  recommendation: string | null
  fetched_at: string | null
}

interface AISISummaryResp {
  total: number
  count_by_source: Record<string, number>
  count_by_cap_area: Record<string, number>
  count_meeting_threshold: number
  latest_publication_date: string | null
  latest_5: AISIEvaluationRow[]
  generated_at: string
}

interface AISIEvaluationsResp {
  items: AISIEvaluationRow[]
  count: number
  filters: { source: string | null; cap_area: string | null; model: string | null; since: string | null }
  generated_at: string
}

// ── Small UI atoms ──────────────────────────────────────────────────────────

function LiveBadge() {
  return (
    <span className="badge text-[9px] bg-cyan/10 text-cyan border border-cyan/40 uppercase tracking-widest">
      live
    </span>
  )
}

function StaticBadge() {
  return (
    <span className="badge text-[9px] bg-amber/10 text-amber-light border border-amber/40 uppercase tracking-widest">
      static
    </span>
  )
}

interface PanelProps {
  title: string
  icon: LucideIcon
  variant: 'live' | 'static'
  children: React.ReactNode
  className?: string
}

function Panel({ title, icon: Icon, variant, children, className }: PanelProps) {
  return (
    <section
      className={clsx(
        'card p-4 border',
        variant === 'live' ? 'border-cyan/30' : 'border-amber/30',
        className,
      )}
    >
      <header className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
        <Icon size={14} className={variant === 'live' ? 'text-cyan' : 'text-amber-light'} />
        <h2 className="text-xs font-semibold uppercase tracking-widest text-text-primary">
          {title}
        </h2>
        {variant === 'live' ? <LiveBadge /> : <StaticBadge />}
      </header>
      {children}
    </section>
  )
}

interface SourceChipsProps {
  sources: Record<string, number> | undefined
  className?: string
  // Render every regulator chip (no top-N truncation). Used on the
  // Banking-vertical panel where the breadth is the point of the tile.
  showAll?: boolean
}

// Regulator-specific chip palette. Reuses the existing tone tokens so
// the chips match the rest of the dashboard. Unknown sources fall back
// to the muted/border default.
const SOURCE_CHIP_TONE: Record<string, string> = {
  // UK
  boe:        'bg-cyan/10 text-cyan border-cyan/40',
  fca:        'bg-violet/10 text-violet-light border-violet/40',
  pra:        'bg-blue/10 text-blue-light border-blue/40',
  // Europe
  ecb:        'bg-amber/10 text-amber-light border-amber/40',
  // USA
  occ:        'bg-emerald/10 text-emerald border-emerald/40',
  fed:        'bg-rose/10 text-rose-light border-rose/40',
  'sec-8k':   'bg-text-muted/10 text-text-secondary border-border',
  sec_8k:     'bg-text-muted/10 text-text-secondary border-border',
  // Asia-Pacific
  mas:        'bg-emerald/10 text-emerald-light border-emerald/40',
  hkma:       'bg-violet/10 text-violet border-violet/40',
  jfsa:       'bg-rose/10 text-rose border-rose/40',
  // Global
  bis:        'bg-blue/10 text-blue border-blue/40',
  fsb:        'bg-amber/10 text-amber border-amber/40',
  iosco:      'bg-cyan/10 text-cyan-light border-cyan/40',
  // Other
  aiid:       'bg-text-muted/10 text-text-secondary border-border',
  bloomberg:  'bg-text-muted/10 text-text-muted border-border',
}

function chipToneFor(key: string): string {
  return SOURCE_CHIP_TONE[key.toLowerCase()]
    ?? 'bg-surface-raised text-text-muted border-border'
}

function SourceChips({ sources, className, showAll = false }: SourceChipsProps) {
  if (!sources) return null
  const sorted = Object.entries(sources).sort((a, b) => b[1] - a[1])
  const entries = showAll ? sorted : sorted.slice(0, 6)
  if (entries.length === 0) return null
  return (
    <div className={clsx('flex flex-wrap items-center gap-1 mt-2', className)}>
      <span className="text-[9px] uppercase tracking-widest text-text-muted">sources</span>
      {entries.map(([k, v]) => (
        <span
          key={k}
          className={clsx(
            'badge text-[9px] uppercase tracking-wider border',
            chipToneFor(k),
          )}
          title={`${k}: ${v}`}
        >
          {k} <span className="ml-1 opacity-80">{v}</span>
        </span>
      ))}
    </div>
  )
}

function fmtUnit(value: number, unit: string): string {
  if (unit === 'USD_BILLION') return `$${value.toFixed(1)}B`
  if (unit === 'PCT') return `${value.toFixed(1)}%`
  if (unit === 'COUNT') return value.toLocaleString()
  if (unit === 'GW') return `${value.toFixed(1)} GW`
  if (unit === 'TONS_CO2E') return `${value.toLocaleString()} t CO2e`
  return String(value)
}

function metricLabel(metric: string): string {
  return metric.split('_').join(' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function AIRiskLivePage() {
  const [incidents, setIncidents] = useState<IncidentsResp | null>(null)
  const [actors, setActors] = useState<ThreatActorsResp | null>(null)
  const [toxic, setToxic] = useState<ToxicPathsResp | null>(null)
  const [fin, setFin] = useState<FinancialResp | null>(null)
  const [stanford, setStanford] = useState<StanfordResp | null>(null)
  const [hfPapers, setHfPapers] = useState<HfPapersResp | null>(null)
  const [velocity, setVelocity] = useState<FrontierVelocityResp | null>(null)
  const [aisiSummary, setAisiSummary] = useState<AISISummaryResp | null>(null)
  const [aisiEvals, setAisiEvals] = useState<AISIEvaluationsResp | null>(null)
  const [aisiSourceFilter, setAisiSourceFilter] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(() => {
    setLoading(true)
    Promise.allSettled([
      api.get('/api/ai-risk-index/incidents').then(r => setIncidents(r.data)),
      api.get('/api/ai-risk-index/threat-actors').then(r => setActors(r.data)),
      api.get('/api/ai-risk-index/toxic-paths').then(r => setToxic(r.data)),
      api.get('/api/ai-risk-index/financial').then(r => setFin(r.data)),
      api.get('/api/ai-risk-index/stanford-findings').then(r => setStanford(r.data)),
      api.get('/api/ai-risk-index/frontier-velocity').then(r => setVelocity(r.data)).catch(() => {}),
      api.get('/api/frontier/daily-papers').then(r => setHfPapers(r.data)).catch(() => {}),
      api.get('/api/aisi/summary').then(r => setAisiSummary(r.data)).catch(() => {}),
      api.get('/api/aisi/evaluations', { params: { limit: 50 } })
        .then(r => setAisiEvals(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false))
  }, [])
  useEffect(() => { refresh() }, [refresh])

  const sectorRows = useMemo(() => {
    if (!incidents?.by_sector) return []
    const total = Object.values(incidents.by_sector).reduce((a, b) => a + b, 0)
    return Object.entries(incidents.by_sector).map(([k, v]) => ({
      label: k,
      count: v,
      pct: total > 0 ? Math.round((v / total) * 100) : 0,
    }))
  }, [incidents])

  const [digestCopyState, setDigestCopyState] = useState<'idle' | 'ok' | 'err'>('idle')

  // Open the daily AI Risk markdown digest in a new tab. The endpoint
  // returns text/markdown so most browsers render it inline; users can
  // save the page to disk for archival.
  const openDigestMarkdown = () => {
    window.open('/api/ai-risk-index/digest?format=markdown', '_blank', 'noopener,noreferrer')
  }

  // Copy the JSON variant of the digest to the clipboard. Useful for
  // dropping into a deck, ticket, or downstream automation.
  const copyDigestJson = useCallback(async () => {
    try {
      const r = await api.get('/api/ai-risk-index/digest', { params: { format: 'json' } })
      const text = JSON.stringify(r.data, null, 2)
      await navigator.clipboard.writeText(text)
      setDigestCopyState('ok')
      setTimeout(() => setDigestCopyState('idle'), 2000)
    } catch {
      setDigestCopyState('err')
      setTimeout(() => setDigestCopyState('idle'), 2000)
    }
  }, [])

  const downloadCsv = () => {
    const rows: string[] = ['surface,metric,value,unit,notes']
    if (incidents) {
      rows.push(`incidents,total,${incidents.total},count,`)
      rows.push(`incidents,ytd,${incidents.ytd},count,`)
      rows.push(`incidents,yoy_delta_pct,${incidents.yoy_delta_pct},pct,`)
    }
    if (actors) {
      rows.push(`threat_actors,total_actors,${actors.total_actors},count,`)
      rows.push(`threat_actors,uses_ai_count,${actors.uses_ai_count},count,`)
    }
    if (fin) {
      rows.push(`financial,30d,${fin.counts['30d']},count,`)
      rows.push(`financial,60d,${fin.counts['60d']},count,`)
      rows.push(`financial,365d,${fin.counts['365d']},count,`)
    }
    stanford?.findings?.forEach(f => {
      rows.push(`stanford_2026,${f.metric},${f.value},${f.unit},"${(f.notes || '').replace(/"/g, "'")}"`)
    })
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai_risk_index_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        icon={Activity} iconClassName="text-cyan" title="AI RISK INDEX (LIVE)"
        subtitle="powered by our own feeds · live + Stanford 2026 static"
        actions={<>
          <button type="button" onClick={refresh} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button type="button" onClick={openDigestMarkdown} className="btn-ghost"
            title="Opens the daily markdown digest in a new tab">
            <FileText size={11} /> Download daily digest
          </button>
          <button type="button" onClick={copyDigestJson} className="btn-ghost"
            title="Copies the JSON variant of the digest to clipboard">
            <ClipboardCopy size={11} />{' '}
            {digestCopyState === 'ok' ? 'Copied' : digestCopyState === 'err' ? 'Copy failed' : 'Copy as JSON'}
          </button>
          <button type="button" onClick={downloadCsv} className="btn-ghost">
            <Download size={11} /> CSV
          </button>
        </>}
      />

      <p className="text-[11px] text-text-muted">
        Refreshed {incidents?.generated_at ? new Date(incidents.generated_at).toUTCString() : '—'}.
        Cyan border = live from our feeds. Amber border = Stanford 2026 static snapshot.
      </p>

      {/* Row 1 — Incidents + AI-augmented threats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="Incidents" icon={Skull} variant="live">
          {!incidents || incidents.total === 0 ? (
            <p className="text-xs text-text-muted">No data yet — AIID feed empty or unreachable.</p>
          ) : (
            <div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-2xl font-bold text-text-primary">{incidents.ytd}</span>
                <span className="text-[10px] uppercase text-text-muted">YTD</span>
                <span className={clsx('badge text-[10px]',
                  incidents.yoy_delta_pct >= 0
                    ? 'bg-rose/10 text-rose-light border border-rose/30'
                    : 'bg-emerald/10 text-emerald border border-emerald/30')}>
                  {incidents.yoy_delta_pct >= 0 ? '↑' : '↓'} {Math.abs(incidents.yoy_delta_pct).toFixed(1)}% vs prior year
                </span>
              </div>
              <Sparkline
                points={incidents.sparkline_30d.map((v, i) => ({ v, t: String(i) }))}
                tone="rose"
                width={220}
                height={32}
                showDot
              />
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest text-text-muted mb-1">By sector</p>
                <ul className="space-y-0.5">
                  {sectorRows.slice(0, 6).map(s => (
                    <li key={s.label} className="flex items-center gap-2 text-xs">
                      <span className="text-text-secondary capitalize w-24">{s.label}</span>
                      <span className="text-text-muted">{s.count}</span>
                      <span className="text-text-muted">·</span>
                      <span className="text-text-muted">{s.pct}%</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-[10px] text-text-muted mt-2">
                Total catalogued: {incidents.total} · prev-year-same-window: {incidents.prev_year_same_window}
              </p>
              <SourceChips sources={incidents.by_source} />
            </div>
          )}
        </Panel>

        <Panel title="AI-augmented threats" icon={Network} variant="live">
          {!actors || actors.uses_ai_count === 0 ? (
            <p className="text-xs text-text-muted">No actors flagged yet — refresh the threat-actor catalogue.</p>
          ) : (
            <div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-2xl font-bold text-text-primary">{actors.uses_ai_count}</span>
                <span className="text-[10px] uppercase text-text-muted">actors flagged uses_ai</span>
              </div>
              <p className="text-[10px] text-text-muted mb-2">
                of {actors.total_actors} total in catalogue
              </p>
              <ul className="space-y-1">
                {actors.most_recent_5.map(a => (
                  <li key={a.id} className="text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-text-primary font-semibold">{a.name}</span>
                      <span className="badge text-[9px] bg-surface-raised text-text-muted">
                        {a.sponsor_country || 'unknown'}
                      </span>
                      <span className="badge text-[9px] bg-surface-raised text-text-muted">
                        {a.category}
                      </span>
                    </div>
                    {a.ai_evidence && (
                      <p className="text-[10px] text-text-muted mt-0.5 leading-relaxed line-clamp-2">
                        {a.ai_evidence}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
      </div>

      {/* Row 2 — Toxic combos + capability frontier */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="Toxic AI combos" icon={AlertTriangle} variant="live">
          {!toxic || toxic.count === 0 ? (
            <p className="text-xs text-text-muted">
              {toxic?.note || 'No active AI-augmented attack paths — engine clean or CDM empty.'}
            </p>
          ) : (
            <div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-2xl font-bold text-text-primary">{toxic.count}</span>
                <span className="text-[10px] uppercase text-text-muted">active path(s)</span>
              </div>
              <ul className="space-y-1">
                {toxic.paths.slice(0, 5).map(p => (
                  <li key={p.id} className="text-xs">
                    <div className="flex items-center gap-2">
                      <span className="badge text-[9px] bg-rose/10 text-rose-light border border-rose/30">
                        score {p.score}
                      </span>
                      <span className="text-text-primary font-semibold truncate">{p.title}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>

        <Panel title="Capability frontier" icon={Brain} variant="live">
          <div className="space-y-3">
            {/* Sub-bar 1 — arXiv 30d daily distribution */}
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-lg font-bold text-text-primary">
                  {velocity?.arxiv?.total ?? 0}
                </span>
                <span className="text-[10px] uppercase text-text-muted">
                  arXiv submissions · 30d (cs.AI/LG/CL/CV)
                </span>
                {velocity?.arxiv?.daily_counts?.length ? (
                  <span className="text-[10px] text-text-muted ml-auto">
                    {velocity.arxiv.daily_counts.filter(v => v > 0).length}/{velocity.arxiv.daily_counts.length} days
                  </span>
                ) : null}
              </div>
              {velocity?.arxiv?.daily_counts?.length ? (
                <Sparkline
                  points={velocity.arxiv.daily_counts.map((v, i) => ({ v, t: String(i) }))}
                  tone="blue"
                  width={220}
                  height={28}
                  showDelta
                />
              ) : null}
            </div>

            {/* Sub-bar 2 — HF release velocity, with daily mini-bar */}
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-lg font-bold text-text-primary">
                  {velocity?.hf_releases?.total ?? hfPapers?.count ?? 0}
                </span>
                <span className="text-[10px] uppercase text-text-muted">
                  HF text-gen releases · 30d (&gt;100 dl)
                </span>
                {velocity?.hf_releases?.daily_counts?.length ? (
                  <span className="text-[10px] text-text-muted ml-auto">
                    {velocity.hf_releases.daily_counts.filter(v => v > 0).length}/{velocity.hf_releases.daily_counts.length} days
                  </span>
                ) : null}
              </div>
              {velocity?.hf_releases?.daily_counts?.length ? (
                <MiniBarChart
                  values={velocity.hf_releases.daily_counts}
                  labels={velocity.hf_releases.daily_counts.map((_, i) => `d-${velocity.hf_releases.daily_counts!.length - 1 - i}`)}
                  tone="violet"
                  width={220}
                  height={22}
                  className="mt-1"
                />
              ) : null}
              {velocity?.hf_releases?.top_5?.length ? (
                <ul className="text-[10px] text-text-muted space-y-0 mt-1">
                  {velocity.hf_releases.top_5.slice(0, 3).map(m => (
                    <li key={m.model_id} className="truncate">
                      {m.model_id}{' '}
                      <span className="text-text-secondary">({m.downloads.toLocaleString()})</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {/* Sub-bar 3 — GitHub frontier-stack stars per-repo chips */}
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-lg font-bold text-text-primary">
                  {(velocity?.github?.total_stars ?? 0).toLocaleString()}
                </span>
                <span className="text-[10px] uppercase text-text-muted">
                  GitHub stars · frontier stack
                </span>
                {velocity?.github?.total_delta_30d ? (
                  <span className="text-[10px] text-emerald">
                    +{velocity.github.total_delta_30d.toLocaleString()}/30d
                  </span>
                ) : null}
                {velocity?.github?.auth === false ? (
                  <span className="text-[9px] text-amber-light" title="Set GITHUB_TOKEN to lift the 60 req/hr cap">
                    unauth
                  </span>
                ) : null}
              </div>
              {velocity?.github?.warning ? (
                <p className="text-[10px] text-amber-light mt-1">
                  {velocity.github.warning}
                </p>
              ) : null}
              {velocity?.github?.repos?.length ? (
                <ul className="space-y-1 mt-1">
                  {[...velocity.github.repos]
                    .sort((a, b) => (b.delta_30d ?? 0) - (a.delta_30d ?? 0))
                    .slice(0, 5)
                    .map(r => {
                      const stars = r.current_stars ?? r.stars ?? 0
                      return (
                        <li key={r.repo} className="flex items-center gap-2 text-[10px]">
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-text-primary hover:text-cyan truncate max-w-[180px]"
                            title={r.repo}
                          >
                            {r.repo}
                          </a>
                          <span className="text-text-muted ml-auto font-mono">
                            {stars.toLocaleString()}
                          </span>
                          <span
                            className={clsx(
                              'badge text-[9px] border',
                              (r.delta_30d ?? 0) > 0
                                ? 'bg-emerald/10 text-emerald border-emerald/30'
                                : 'bg-surface-raised text-text-muted border-border',
                            )}
                          >
                            {r.delta_30d > 0 ? '+' : ''}
                            {(r.delta_30d ?? 0).toLocaleString()}
                            {r.delta_partial ? '+' : ''}/30d
                          </span>
                        </li>
                      )
                    })}
                </ul>
              ) : null}
            </div>
          </div>
        </Panel>
      </div>

      {/* Row 3 — Investment / adoption (static) + Compute (static) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="Investment / adoption" icon={Cpu} variant="static">
          {!stanford ? (
            <p className="text-xs text-text-muted">Loading Stanford 2026 figures…</p>
          ) : (
            <div className="space-y-1.5">
              {stanford.findings
                .filter(f => f.metric === 'us_private_ai_investment_usd_billion'
                          || f.metric === 'global_genai_adoption_pct')
                .map(f => (
                  <div key={f.metric} className="text-xs">
                    <span className="text-2xl font-bold text-text-primary">{fmtUnit(f.value, f.unit)}</span>{' '}
                    <span className="text-[10px] uppercase text-text-muted">{metricLabel(f.metric)}</span>
                    {f.notes && <p className="text-[10px] text-text-muted">{f.notes}</p>}
                  </div>
                ))}
            </div>
          )}
        </Panel>

        <Panel title="Compute / data centres" icon={Database} variant="static">
          {!stanford ? (
            <p className="text-xs text-text-muted">Loading Stanford 2026 figures…</p>
          ) : (
            <div className="space-y-1.5">
              {stanford.findings
                .filter(f => f.metric === 'global_data_centre_consumption_gw'
                          || f.metric === 'us_data_centres')
                .map(f => (
                  <div key={f.metric} className="text-xs">
                    <span className="text-2xl font-bold text-text-primary">{fmtUnit(f.value, f.unit)}</span>{' '}
                    <span className="text-[10px] uppercase text-text-muted">{metricLabel(f.metric)}</span>
                    {f.notes && <p className="text-[10px] text-text-muted">{f.notes}</p>}
                  </div>
                ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Row 4 — Banking-vertical lens */}
      <Panel title="Banking-vertical lens" icon={Building2} variant="live">
        {!fin ? (
          <p className="text-xs text-text-muted">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Counts</p>
              <ul className="space-y-0.5 text-xs">
                <li><span className="text-text-primary font-semibold">{fin.counts['30d']}</span> in last 30d</li>
                <li><span className="text-text-primary font-semibold">{fin.counts['60d']}</span> in last 60d</li>
                <li><span className="text-text-primary font-semibold">{fin.counts['365d']}</span> in last 365d</li>
              </ul>
              <p className="text-[10px] text-text-muted mt-2">
                {fin.total_finance} total finance-sector items in catalogue
              </p>
              <MiniBarChart
                values={[fin.counts['30d'], fin.counts['60d'], fin.counts['365d']]}
                labels={['30d', '60d', '365d']}
                tone="cyan"
                width={120}
                height={24}
                className="mt-2"
              />
              <SourceChips sources={fin.by_source} showAll />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Top control gaps</p>
              {fin.top_control_gaps.length === 0 ? (
                <p className="text-xs text-text-muted">No control-gap signals detected yet.</p>
              ) : (
                <ul className="space-y-0.5 text-xs">
                  {fin.top_control_gaps.map(g => (
                    <li key={g.label}>
                      <span className="text-text-primary">{g.label}</span>{' '}
                      <span className="text-text-muted">({g.hits} hits)</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Top BoE-relevant items</p>
              {fin.top_boe_relevant.length === 0 ? (
                <p className="text-xs text-text-muted">No items in window.</p>
              ) : (
                <ul className="space-y-1 text-xs">
                  {fin.top_boe_relevant.map((b, i) => (
                    <li key={i} className="leading-snug">
                      <span className="text-[10px] text-text-muted font-mono">{b.date || '—'}</span>{' '}
                      {b.source && (
                        <span
                          className={clsx(
                            'badge text-[9px] uppercase tracking-wider border mr-1',
                            chipToneFor(b.source),
                          )}
                          title={b.source}
                        >
                          {b.source}
                        </span>
                      )}
                      <span className="text-text-primary">{b.title}</span>
                      {b.source_url && (
                        <a href={b.source_url} target="_blank" rel="noreferrer"
                          aria-label="Open source"
                          className="inline-flex items-center gap-1 text-blue-light hover:underline ml-1">
                          <ExternalLink size={9} />
                          <span className="sr-only">Open source</span>
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Panel>

      {/* Row 5 — Stanford headline figures table (static) */}
      <Panel title="Stanford headline figures (2026)" icon={Zap} variant="static">
        {!stanford || stanford.count === 0 ? (
          <p className="text-xs text-text-muted">No findings loaded.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                  <th className="text-left py-1 pr-2">Metric</th>
                  <th className="text-right py-1 pr-2">Value</th>
                  <th className="text-left py-1 pr-2">Unit</th>
                  <th className="text-left py-1">Notes</th>
                </tr>
              </thead>
              <tbody>
                {stanford.findings.map(f => (
                  <tr key={f.metric} className="border-b border-border/40">
                    <td className="py-1 pr-2 text-text-primary">{metricLabel(f.metric)}</td>
                    <td className="py-1 pr-2 text-right font-mono text-text-primary">{fmtUnit(f.value, f.unit)}</td>
                    <td className="py-1 pr-2 text-text-muted">{f.unit}</td>
                    <td className="py-1 text-text-muted">{f.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-text-muted mt-2">
              <a href={stanford.source_url} target="_blank" rel="noreferrer" className="hover:underline text-blue-light">
                {stanford.source}
              </a>{' '}
              · year {stanford.year} · loaded from {stanford.from || 'db'}
            </p>
          </div>
        )}
      </Panel>

      {/* Row 6 — AISI Evaluations panel (Track 24) */}
      <AISIEvaluationsPanel
        summary={aisiSummary}
        evaluations={aisiEvals}
        sourceFilter={aisiSourceFilter}
        onSourceFilter={setAisiSourceFilter}
      />
    </div>
  )
}

// ── AISI Evaluations Panel (Track 24) ──────────────────────────────────────

const AISI_SOURCE_LABELS: Record<string, string> = {
  uk_aisi:      'UK',
  us_aisi:      'US',
  jp_aisi:      'JP',
  eu_ai_office: 'EU',
}

const AISI_SOURCE_TONE: Record<string, string> = {
  uk_aisi:      'bg-cyan/10 text-cyan border-cyan/40',
  us_aisi:      'bg-rose/10 text-rose-light border-rose/40',
  jp_aisi:      'bg-amber/10 text-amber-light border-amber/40',
  eu_ai_office: 'bg-blue/10 text-blue-light border-blue/40',
}

const AISI_CAP_TONE: Record<string, string> = {
  cyber:      'bg-rose/10 text-rose-light border-rose/40',
  bio:        'bg-emerald/10 text-emerald-light border-emerald/40',
  chem:       'bg-violet/10 text-violet-light border-violet/40',
  autonomy:   'bg-cyan/10 text-cyan border-cyan/40',
  persuasion: 'bg-amber/10 text-amber-light border-amber/40',
  general:    'bg-text-muted/10 text-text-secondary border-border',
}

interface AISIPanelProps {
  summary: AISISummaryResp | null
  evaluations: AISIEvaluationsResp | null
  sourceFilter: string | null
  onSourceFilter: (s: string | null) => void
}

function AISIEvaluationsPanel({
  summary, evaluations, sourceFilter, onSourceFilter,
}: AISIPanelProps) {
  const total = summary?.total ?? 0
  const latest = summary?.latest_publication_date
    ? new Date(summary.latest_publication_date).toISOString().slice(0, 10)
    : '—'
  const meetingThreshold = summary?.count_meeting_threshold ?? 0
  const cyberCount = summary?.count_by_cap_area?.cyber ?? 0

  // Build the recent-evaluations list — apply UI-side source filter to
  // avoid an extra round-trip per chip click.
  const recentRows = (evaluations?.items ?? []).filter(
    r => !sourceFilter || r.source === sourceFilter,
  ).slice(0, 8)

  // Cap-area bar chart input — derived from summary.count_by_cap_area
  // (already DB-aggregated). Fixed display order for stable layout.
  const capAreaOrder = ['cyber', 'bio', 'chem', 'autonomy', 'persuasion', 'general']
  const capCounts = capAreaOrder.map(k => summary?.count_by_cap_area?.[k] ?? 0)

  // Source chips — every source AISI publishes from, even when zero rows
  // have landed yet (so the filter row is stable across cache warm-ups).
  const sourceKeys = ['uk_aisi', 'us_aisi', 'jp_aisi', 'eu_ai_office']

  return (
    <Panel title="AISI Evaluations — UK / US / JP / EU" icon={ShieldCheck} variant="live">
      {/* Source-chip filter row */}
      <div className="flex flex-wrap items-center gap-1 mb-3">
        <span className="text-[9px] uppercase tracking-widest text-text-muted">source</span>
        <button
          type="button"
          onClick={() => onSourceFilter(null)}
          className={clsx(
            'badge text-[9px] uppercase tracking-wider border',
            sourceFilter === null
              ? 'bg-cyan/20 text-cyan border-cyan/60'
              : 'bg-surface-raised text-text-muted border-border',
          )}
        >
          all
          <span className="ml-1 opacity-80">{total}</span>
        </button>
        {sourceKeys.map(sk => {
          const c = summary?.count_by_source?.[sk] ?? 0
          const isActive = sourceFilter === sk
          return (
            <button
              key={sk}
              type="button"
              onClick={() => onSourceFilter(isActive ? null : sk)}
              className={clsx(
                'badge text-[9px] uppercase tracking-wider border',
                isActive
                  ? AISI_SOURCE_TONE[sk]
                  : 'bg-surface-raised text-text-muted border-border',
              )}
              title={`${sk}: ${c} evaluations`}
            >
              {AISI_SOURCE_LABELS[sk]}
              <span className="ml-1 opacity-80">{c}</span>
            </button>
          )
        })}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="rounded border border-border bg-surface-raised/40 p-2">
          <p className="text-[9px] uppercase tracking-widest text-text-muted">Total evals</p>
          <p className="text-xl font-bold text-text-primary">{total}</p>
        </div>
        <div className="rounded border border-border bg-surface-raised/40 p-2">
          <p className="text-[9px] uppercase tracking-widest text-text-muted">Latest publication</p>
          <p className="text-base font-mono text-text-primary">{latest}</p>
        </div>
        <div className="rounded border border-border bg-surface-raised/40 p-2">
          <p className="text-[9px] uppercase tracking-widest text-text-muted">Meets threshold</p>
          <p className={clsx(
            'text-xl font-bold',
            meetingThreshold > 0 ? 'text-rose-light' : 'text-text-primary',
          )}>
            {meetingThreshold}
            {meetingThreshold > 0 && <AlertTriangle size={12} className="inline ml-1 text-rose-light" />}
          </p>
        </div>
        <div className="rounded border border-border bg-surface-raised/40 p-2">
          <p className="text-[9px] uppercase tracking-widest text-text-muted">Cyber cap-area</p>
          <p className="text-xl font-bold text-cyan">{cyberCount}</p>
        </div>
      </div>

      {/* Cap-area breakdown bar chart */}
      <div className="mb-3">
        <p className="text-[10px] uppercase tracking-widest text-text-muted mb-1">
          Breakdown by capability area
        </p>
        <MiniBarChart
          values={capCounts}
          labels={capAreaOrder}
          width={220}
          height={28}
          tone="violet"
          emptyHint="no evaluations yet — scraper warming"
        />
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
          {capAreaOrder.map((k, i) => (
            <span key={k} className="text-[10px] text-text-muted">
              <span className="inline-block w-2 h-2 rounded-sm bg-violet/60 mr-1" />
              {k} <span className="font-mono text-text-secondary">{capCounts[i]}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Recent evaluations list — top 8 by date desc */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-text-muted mb-1">
          Recent evaluations
          {sourceFilter && (
            <span className="ml-1 normal-case text-text-secondary">
              · filtered: {AISI_SOURCE_LABELS[sourceFilter]}
            </span>
          )}
        </p>
        {recentRows.length === 0 ? (
          <p className="text-xs text-text-muted">
            No evaluations ingested yet — the 6h scraper may still be on its first warm-up.
          </p>
        ) : (
          <ul className="space-y-1 text-xs">
            {recentRows.map(r => (
              <li key={r.id} className="flex items-baseline gap-2 leading-snug">
                <span className="text-[10px] font-mono text-text-muted shrink-0">
                  {r.publication_date ? r.publication_date.slice(0, 10) : '—'}
                </span>
                <span
                  className={clsx(
                    'badge text-[9px] uppercase tracking-wider border shrink-0',
                    AISI_SOURCE_TONE[r.source] ?? 'bg-text-muted/10 text-text-secondary border-border',
                  )}
                >
                  {AISI_SOURCE_LABELS[r.source] ?? r.source}
                </span>
                <span className="text-text-primary truncate" title={r.model}>
                  {r.model || r.title}
                </span>
                <span
                  className={clsx(
                    'badge text-[9px] uppercase tracking-wider border shrink-0',
                    AISI_CAP_TONE[r.cap_area] ?? 'bg-text-muted/10 text-text-secondary border-border',
                  )}
                >
                  {r.cap_area}
                </span>
                {r.meets_threshold && (
                  <AlertTriangle size={11} className="text-rose-light shrink-0"
                    aria-label="Meets threshold of concern" />
                )}
                {r.pdf_url && (
                  <a
                    href={r.pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue-light hover:underline ml-auto shrink-0"
                    title="Open AISI publication PDF / source"
                  >
                    <ExternalLink size={10} />
                    <span className="sr-only">Open source</span>
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-[10px] text-text-muted mt-2">
        Sources: UK AISI · US AISI (NIST CAISI) · Japan AISI · EU AI Office. 6h Redis cache.
        Threshold flag is LLM-extracted from each publication; click the arrow to open the PDF or detail page.
      </p>
    </Panel>
  )
}
